import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import { formatCurrency } from "@/utils/formatCurrency";
import { ScenarioPoint } from "@trader-dashboard/shared";

type DrawdownScenarioChartProps = {
  curve: ScenarioPoint[];
  equity: number | undefined;
  referencePips: number;
};

const ScenarioTooltip = ({
  active, payload, label, equity,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: number;
  equity: number | undefined;
}): React.ReactNode => {
  if (!active || !payload?.length) return null;
  const loss = payload[0].value;
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-md">
      <p className="text-xs font-medium">{label} pips against</p>
      <p className="mt-1 text-sm font-bold tabular-nums text-red-600">{formatCurrency(loss)}</p>
      {equity && equity > 0 && (
        <p className="text-xs text-muted-foreground">
          {((loss / equity) * 100).toFixed(1)}% of equity
        </p>
      )}
    </div>
  );
};

/**
 * The stress curve assumes every open position moves against the book at once
 * — a correlated worst case, so it reads as a floor rather than a forecast.
 */
export const DrawdownScenarioChart = ({
  curve,
  equity,
  referencePips,
}: DrawdownScenarioChartProps): React.ReactNode => {
  if (curve.length === 0) {
    return (
      <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
        No priceable open positions
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <AreaChart data={curve}>
        <defs>
          <linearGradient id="drawdownGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dc2626" stopOpacity={0} />
            <stop offset="100%" stopColor="#dc2626" stopOpacity={0.3} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="pips"
          tick={{ fontSize: 11, fill: "#9ca3af" }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${v}p`}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "#9ca3af" }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`}
        />
        <ReferenceLine y={0} stroke="#d1d5db" />
        {equity && equity > 0 && (
          <ReferenceLine
            y={-equity}
            stroke="#dc2626"
            strokeDasharray="4 4"
            label={{ value: "Equity wiped out", fontSize: 10, fill: "#dc2626", position: "insideTopRight" }}
          />
        )}
        <ReferenceLine
          x={referencePips}
          stroke="#9ca3af"
          strokeDasharray="3 3"
        />
        <Tooltip content={<ScenarioTooltip equity={equity} />} />
        <Area
          type="monotone"
          dataKey="loss"
          stroke="#dc2626"
          strokeWidth={2}
          fill="url(#drawdownGradient)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
